import { useMemo } from "react";
import { format, parseISO, isValid as isValidDate } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";

/**
 * Native-input date picker. Works everywhere, in every Modal/Sheet/Popover
 * combination, without Radix portal or focus-trap conflicts. Users can type
 * the date directly, use the browser's built-in calendar UI, or paste it.
 *
 * Props:
 *   value        current ISO string ("YYYY-MM-DD" or full ISO with time)
 *   onChange     called with an ISO string or null to clear
 *   withTime     true → datetime-local input, false → date input (default false)
 *   placeholder  displayed when empty (not visible in native input; kept for API)
 *   disabled     boolean
 *   testId       forwarded as data-testid
 */
export default function DatePicker({
  value,
  onChange,
  withTime = false,
  placeholder,
  disabled = false,
  testId,
}) {
  // Turn the incoming ISO into the format that <input type="date" | "datetime-local"> expects.
  const inputValue = useMemo(() => {
    if (!value) return "";
    try {
      const d = typeof value === "string" ? parseISO(value) : value;
      if (!isValidDate(d)) return "";
      return format(d, withTime ? "yyyy-MM-dd'T'HH:mm" : "yyyy-MM-dd");
    } catch {
      return "";
    }
  }, [value, withTime]);

  const handleChange = (e) => {
    const raw = e.target.value;
    if (!raw) {
      onChange(null);
      return;
    }
    try {
      // Interpret the naive local value; store as ISO.
      const parsed = withTime
        ? parseISO(raw)                  // yyyy-MM-ddTHH:mm  (local)
        : parseISO(`${raw}T00:00:00`);   // date-only → midnight local
      if (isValidDate(parsed)) {
        onChange(withTime ? parsed.toISOString() : raw);
      }
    } catch { /* ignore */ }
  };

  const clear = () => onChange(null);

  return (
    <div className="relative flex items-center w-full">
      <CalendarIcon
        className="absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none"
        strokeWidth={1.75}
      />
      <input
        type={withTime ? "datetime-local" : "date"}
        value={inputValue}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full h-9 pl-9 pr-8 rounded-md border border-input bg-background text-sm hover:border-primary/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors disabled:opacity-50"
      />
      {inputValue && !disabled && (
        <button
          type="button"
          onClick={clear}
          data-testid={testId ? `${testId}-clear` : undefined}
          className="absolute right-2 h-6 w-6 inline-flex items-center justify-center rounded hover:bg-accent"
          aria-label="Clear date"
        >
          <X className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      )}
    </div>
  );
}
